import React, {useState} from 'react';
import {Alert, AlertProps, Box, Button, ColumnLayout, Header, SpaceBetween} from '@amzn/awsui-components-react';
import {ConfirmationModal} from './confirmation-modal';
import '../../assets/styles/file-download-upload.scss';
import {TemplateDropzone} from './template-dropzone';
import {DownloadTemplateButton} from './download-template-button';
import {Notification} from '../navigation/page-layout';
import {BusinessId, Country, Flow, ServiceResource, useAuth} from '../hooks/use-auth';
import {
    useAddEmailIdentity,
    useDeleteEmailIdentity,
    useGetEmailIdentity,
    useHasBulkOverrideTemplate,
    useListBulkOverrideTemplateForTags,
    useResetOverrides,
} from '../hooks/use-forecast-store-api';
import {translateErrorToReactNode} from '../common';
import {ButtonWithConfirmation} from '../common/button-with-confirmation';

interface BulkOverrideProps {
    showResetOverrideButton: boolean;
    pushNotification: (notification: Notification) => void;
    resetOverridesAlertStay: boolean;
    bulkOverrideTemplateTag: string[];
    additionalTemplateDefinitions: TemplateDefinition[];
    contentClassName: string;
    hideNote?: boolean;
    resetButtonLabel?: string;
    resetModalHeader?: string;
    resetModalPrompt?: string;
    resetConfirmPhrase?: string;
    onResetExecute?: () => Promise<void>;
    showDowResetButton?: boolean;
    dowResetButtonLabel?: string;
    dowResetModalHeader?: string;
    dowResetModalPrompt?: string;
    dowResetConfirmPhrase?: string;
    onDowResetExecute?: () => Promise<void>;
}

interface TemplateDefinition {
    templateId: string;
    downloadButton: string;
    portalDescription: string;
    useBulkOverrideV2: boolean;
}

const MARKETING_EVENT_TEMPLATE_ID = 'marketing_event_tune';
const MARKETING_EVENT_TEMPLATE_DOWNLOAD_BUTTON = 'Marketing Template';
const MARKETING_EVENT_PORTAL_DESCRIPTION = 'MARKETING';
const CPT_OFFSETS_TEMPLATE_ID = 'cpt_offsets';
const CPT_OFFSETS_TEMPLATE_DOWNLOAD_BUTTON = 'CPT Offsets Template';
const CPT_OFFSETS_PORTAL_DESCRIPTION = 'CPT OFFSETS';
const DOW_RATIO_TEMPLATE_ID = 'DAY_OF_WEEK_RATIO';

const CPT_OFFSETS_ENABLED_COUNTRIES = new Set(['US', 'DE', 'IT', 'ES', 'GB']);

/**
 * Internal bulk override page backed by Bulk Override Automation APIs (v2 and legacy)
 *
 * Visible for testing
 */
export function BulkOverride(props: BulkOverrideProps) {
    const auth = useAuth();

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);
    const forecastStoreEditClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreEdit);

    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const [dowConfirmationModalVisible, setDowConfirmationModalVisible] = useState(false);
    const [alert, setAlert] = useState({
        visible: false,
        type: 'success' as AlertProps.Type,
        header: undefined as React.ReactNode | undefined,
        action: undefined as React.ReactNode | undefined,
        message: '',
    });
    const [emailAlertVisible, setEmailAlertVisible] = useState(true);

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {execute: executeResetOverrides} = useResetOverrides(
        forecastStoreViewClientConfiguration,
        forecastStoreEditClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
        },
        createErrorListener('ResetOverrides failed'),
        [auth]
    );

    const {value: listBulkOverrideTemplateResponse} = useListBulkOverrideTemplateForTags(
        forecastStoreEditClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            bulkOverrideTemplateTags: props.bulkOverrideTemplateTag,
        },
        true,
        createErrorListener('ListBulkOverrideTemplate failed'),
        [auth]
    );

    const {value: getEmailIdentityResponse} = useGetEmailIdentity(
        forecastStoreEditClientConfiguration,
        createErrorListener('GetEmailIdentity failed'),
        [auth]
    );

    const {execute: executeAddEmailIdentity} = useAddEmailIdentity(
        forecastStoreEditClientConfiguration,
        createErrorListener('AddEmailIdentity failed'),
        [auth]
    );

    const {execute: executeDeleteEmailIdentity} = useDeleteEmailIdentity(
        forecastStoreEditClientConfiguration,
        createErrorListener('DeleteEmailIdentity failed'),
        [auth]
    );

    function createOnChange(header: string) {
        return (message: string, type: AlertProps.Type, action?: React.ReactNode) => {
            setAlert({
                visible: true,
                type: type,
                header: header,
                action: action,
                message: message,
            });
        };
    }

    const onChangeResetOverrides = createOnChange('Reset Overrides Status');
    const resetOverrides = async () => {
        setConfirmationModalVisible(false);
        try {
            onChangeResetOverrides('Resetting the overrides is currently in process. See Forecast History tab for status.', 'info');

            await executeResetOverrides();

            if (!props.resetOverridesAlertStay) {
                setTimeout(() => {
                    setAlert((prevState) => ({
                        ...prevState,
                        visible: false,
                    }));
                }, 20000);
            }
        } catch (error) {
            onChangeResetOverrides('There is a service error. Please contact someone from the support team.', 'error');
        }
    };

    const addEmail = async () => {
        await executeAddEmailIdentity();
    };

    const deleteEmail = async () => {
        await executeDeleteEmailIdentity();
    };

    const templateDefinitions = (listBulkOverrideTemplateResponse || []).map((template) => ({
        templateId: template.templateId,
        downloadButton: template.downloadButton,
        portalDescription: template.portalDescription,
        useBulkOverrideV2: true,
    }));

    const allTemplateDefinitions = props.additionalTemplateDefinitions.concat(templateDefinitions);

    const allTemplateDefinitionsByName = Array.from(
        allTemplateDefinitions
            .reduce(
                (entryMap, e) => entryMap.set(e.portalDescription, [...(entryMap.get(e.portalDescription) || []), e]),
                new Map<string, TemplateDefinition[]>()
            )
            .entries()
    );

    const resetOverrideButton = props.showResetOverrideButton ? (
        <React.Fragment>
            <Button variant={'primary'} data-testid="reset-overrides-button" onClick={() => setConfirmationModalVisible(true)}>
                {props.resetButtonLabel || 'Reset All Overrides'}
            </Button>
            <ConfirmationModal
                visible={confirmationModalVisible}
                promptHeader={props.resetModalHeader || 'Reset All Overrides'}
                prompt={props.resetModalPrompt || 'Are you sure you want to delete all overrides for this forecast?'}
                confirmPhrase={props.resetConfirmPhrase || 'delete'}
                onCancel={() => setConfirmationModalVisible(false)}
                onConfirm={
                    props.onResetExecute
                        ? async () => {
                              setConfirmationModalVisible(false);
                              await props.onResetExecute!();
                          }
                        : resetOverrides
                }
            />
        </React.Fragment>
    ) : (
        <React.Fragment />
    );

    const dowResetOverrideButton = props.showDowResetButton ? (
        <React.Fragment>
            <Button
                variant={'primary'}
                data-testid="reset-dow-ratio-overrides-button"
                onClick={() => setDowConfirmationModalVisible(true)}
            >
                {props.dowResetButtonLabel || 'Reset DOW Ratio Overrides'}
            </Button>
            <ConfirmationModal
                visible={dowConfirmationModalVisible}
                promptHeader={props.dowResetModalHeader || 'Reset DOW Ratio Overrides'}
                prompt={
                    props.dowResetModalPrompt ||
                    'Are you sure you want to reset all day-of-week ratio overrides for this forecast? ' +
                        'Other overrides will not be affected.'
                }
                confirmPhrase={props.dowResetConfirmPhrase || 'reset'}
                onCancel={() => setDowConfirmationModalVisible(false)}
                onConfirm={async () => {
                    setDowConfirmationModalVisible(false);
                    await props.onDowResetExecute!();
                }}
            />
        </React.Fragment>
    ) : (
        <React.Fragment />
    );

    return (
        <React.Fragment>
            <Alert
                data-testid="email-alert"
                dismissible
                visible={emailAlertVisible}
                action={
                    <ButtonWithConfirmation
                        label={getEmailIdentityResponse?.verified ? 'Disable' : 'Enable'}
                        variant={'normal'}
                        disabled={false}
                        promptHeader={'Email Notification'}
                        prompt={
                            getEmailIdentityResponse?.verified
                                ? 'Your email has been removed and you will no longer receive emails for your future interactions.'
                                : 'Your email has been added to receive notifications of your Excelsior interactions. ' +
                                  'You will shortly receive a verification email that will be sent from an AWS. ' +
                                  'Please click the link to finish the enabling for emails process.'
                        }
                        confirmPhrase={''}
                        onConfirm={() => {
                            setEmailAlertVisible(false);
                            if (getEmailIdentityResponse?.verified) {
                                deleteEmail();
                            } else {
                                addEmail();
                            }
                        }}
                        showInput={false}
                        showCancel={false}
                    />
                }
                onDismiss={() => setEmailAlertVisible(false)}
                header={'Excelsior Emailing'}
            >
                Excelsior can now send emails with details of your interactions directly to the user upon the outcome. If you wish
                to change your email notification preference, please Enable/Disable by selecting the button.
            </Alert>
            <Alert
                onDismiss={() =>
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        visible: false,
                    }))
                }
                visible={alert.visible}
                dismissAriaLabel="Close alert"
                dismissible
                type={alert.type}
                header={alert.header}
                action={alert.action}
            >
                <div className={'error-message'}>{alert.message}</div>
            </Alert>
            <div className="overrides-header">
                <Box float="right">
                    <SpaceBetween direction="vertical" size="xs" alignItems="center">
                        {resetOverrideButton}
                        {dowResetOverrideButton}
                    </SpaceBetween>
                </Box>
            </div>
            <div className={props.contentClassName}>
                <ColumnLayout columns={allTemplateDefinitionsByName.length} disableGutters>
                    {allTemplateDefinitionsByName.map((entry) => {
                        return (
                            <div className="center" key={entry[0]}>
                                <div className="centered-header">
                                    <Header variant={'h1'}>{entry[0]}</Header>
                                </div>
                                <br />
                                {entry[1].map((template) => {
                                    return (
                                        <>
                                            <DownloadTemplateButton
                                                templateName={template.downloadButton}
                                                templateType={template.templateId}
                                                useBulkOverrideV2={template.useBulkOverrideV2}
                                                pushNotification={props.pushNotification}
                                            />
                                            <div className="uploadsContainer" style={{height: 350 / entry[1].length}}>
                                                <div className={'upload'}>
                                                    <TemplateDropzone
                                                        templateType={template.templateId}
                                                        useBulkOverrideV2={template.useBulkOverrideV2}
                                                        pushNotification={props.pushNotification}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    );
                                })}
                            </div>
                        );
                    })}
                </ColumnLayout>
            </div>
            <div className={'note center'} hidden={props.hideNote ?? false}>
                <Alert statusIconAriaLabel="Info">Note: These uploads will take precedence over any previous overrides.</Alert>
            </div>
        </React.Fragment>
    );
}

export function Overrides(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);
    const forecastStoreEditClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreEdit);

    const {execute: executeResetDowRatioOverrides} = useResetOverrides(
        forecastStoreViewClientConfiguration,
        forecastStoreEditClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            resetType: 'dowRatio',
        },
        (e: any) => {
            props.pushNotification({
                type: 'error',
                content: String(e),
                header: 'ResetDowRatioOverrides failed',
            });
        },
        [auth]
    );

    const resetDowRatioOverrides = async () => {
        await executeResetDowRatioOverrides();
    };

    const {value: hasDowRatioTemplate} = useHasBulkOverrideTemplate(
        forecastStoreEditClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            templateId: DOW_RATIO_TEMPLATE_ID,
        },
        // Failing to determine whether a DOW template is registered should not surface a
        // user-facing error - the button just stays hidden (fail closed).
        () => {},
        [auth]
    );

    return (
        <BulkOverride
            showResetOverrideButton={true}
            pushNotification={props.pushNotification}
            bulkOverrideTemplateTag={['bulk_override', 'reset_override']}
            resetOverridesAlertStay={false}
            additionalTemplateDefinitions={[]}
            contentClassName={'content'}
            showDowResetButton={hasDowRatioTemplate ?? false}
            dowResetButtonLabel="Reset DOW Ratio Overrides"
            dowResetModalHeader="Reset DOW Ratio Overrides"
            dowResetModalPrompt="Are you sure you want to reset all day-of-week ratio overrides for this forecast? Other overrides will not be affected."
            dowResetConfirmPhrase="reset"
            onDowResetExecute={resetDowRatioOverrides}
        />
    );
}

export function InputSignal(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);
    const forecastStoreEditClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreEdit);

    const resetPercentageAdjustmentEnabled =
        auth.authInformation!.current!.configurationData?.resetPercentageAdjustmentEnabled ?? false;

    const {execute: executeResetPercentageAdjustments} = useResetOverrides(
        forecastStoreViewClientConfiguration,
        forecastStoreEditClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            resetType: 'percentageAdjustments',
        },
        (e: any) => {
            props.pushNotification({
                type: 'error',
                content: String(e),
                header: 'ResetInputSignalAdjustments failed',
            });
        },
        [auth]
    );

    const resetPercentageAdjustments = async () => {
        await executeResetPercentageAdjustments();
    };

    const additionalTemplateDefinitions = getInputSignalAdditionalTemplateDefinitions(
        auth.authInformation!.current!.businessId,
        auth.authInformation!.current!.country,
        auth.authInformation!.current!.flow
    );

    return (
        <BulkOverride
            showResetOverrideButton={resetPercentageAdjustmentEnabled}
            resetButtonLabel="Reset All Percentage Adjustments"
            resetModalHeader="Reset All Percentage Adjustments"
            resetModalPrompt="Are you sure you want to reset all percentage adjustments for this forecast?"
            resetConfirmPhrase="reset"
            onResetExecute={resetPercentageAdjustments}
            pushNotification={props.pushNotification}
            bulkOverrideTemplateTag={['input_signal']}
            resetOverridesAlertStay={false}
            additionalTemplateDefinitions={additionalTemplateDefinitions}
            contentClassName={'contentSignals'}
            hideNote={true}
        />
    );
}

/**
 * These are the legacy templates.
 */
function getInputSignalAdditionalTemplateDefinitions(businessId: string, country: string, flow: string) {
    const marketingTemplateDefinition: TemplateDefinition = {
        templateId: MARKETING_EVENT_TEMPLATE_ID,
        downloadButton: MARKETING_EVENT_TEMPLATE_DOWNLOAD_BUTTON,
        portalDescription: MARKETING_EVENT_PORTAL_DESCRIPTION,
        useBulkOverrideV2: false,
    };

    const cptOffsetsTemplateDefinition: TemplateDefinition = {
        templateId: CPT_OFFSETS_TEMPLATE_ID,
        downloadButton: CPT_OFFSETS_TEMPLATE_DOWNLOAD_BUTTON,
        portalDescription: CPT_OFFSETS_PORTAL_DESCRIPTION,
        useBulkOverrideV2: false,
    };

    const templateDefinitions: TemplateDefinition[] = [];

    if (businessId === BusinessId.WFM) {
        templateDefinitions.push(marketingTemplateDefinition);
    }

    if (
        (businessId === BusinessId._3P && country === Country.US && flow === Flow.OUTBOUND) ||
        (businessId === BusinessId._3P && country === Country.JP && flow === Flow.OUTBOUND)
    ) {
        templateDefinitions.push(marketingTemplateDefinition);
    }

    const isCptOffsetsTemplateEnabledForCountry = CPT_OFFSETS_ENABLED_COUNTRIES.has(country);
    if (businessId === BusinessId.UFF && isCptOffsetsTemplateEnabledForCountry && flow === Flow.OUTBOUND) {
        templateDefinitions.push(cptOffsetsTemplateDefinition);
    }

    return templateDefinitions;
}
